import { deleteField, doc, updateDoc, writeBatch } from 'firebase/firestore';
import type { OrgChartPerson, OrgDepartment, OrgLevel } from '../types/purchasing';
import { ORG_DEPARTMENTS, ORG_LEVELS } from '../types/purchasing';
import { db } from './firebase';
import { isPinnedSeat, ORG_SEAT_LABELS, orgSeatOf } from './orgChart';
import { canManage, deriveReportsTo, type ReportingSyncRow } from './orgReporting';

function assertMovable(person: OrgChartPerson): void {
  const seat = orgSeatOf(person);
  if (isPinnedSeat(seat)) {
    const label = ORG_SEAT_LABELS[seat] || 'This seat';
    throw new Error(`${person.displayName} is pinned by the ${label} seat. Change their designation or title to move them.`);
  }
}

/**
 * Save a staffer's layer/group and the manager the reporting rules derive for
 * that placement. Pinned seats and the Executive layer are rejected.
 */
export async function setOrgPlacement(
  person: OrgChartPerson,
  placement: { orgLevel: OrgLevel; orgDepartment: OrgDepartment },
  people: OrgChartPerson[],
): Promise<void> {
  const uid = person.uid.trim();
  if (!uid) throw new Error('Missing user');
  assertMovable(person);
  if (!(ORG_LEVELS as string[]).includes(placement.orgLevel)) throw new Error('Invalid layer');
  if (!(ORG_DEPARTMENTS as string[]).includes(placement.orgDepartment)) throw new Error('Invalid group');
  if (placement.orgLevel === 'executive') {
    throw new Error('Executive seats come from the CEO title or COO / CFO designations, not from placement.');
  }
  const next: OrgChartPerson = { ...person, ...placement };
  const roster = people.map(p => (p.uid === uid ? next : p));
  const managerId = deriveReportsTo(next, roster) || null;
  await updateDoc(doc(db, 'users', uid), {
    orgLevel: placement.orgLevel,
    orgDepartment: placement.orgDepartment,
    reportsToUserId: managerId || deleteField(),
  });
}

/**
 * Explicit manager for a General staffer. Must be someone who can manage them
 * (same group, higher layer); clearing falls back to the rule default.
 */
export async function setReportsToUserId(
  person: OrgChartPerson,
  managerId: string | null,
  people: OrgChartPerson[],
): Promise<void> {
  const uid = person.uid.trim();
  if (!uid) throw new Error('Missing user');
  assertMovable(person);
  const next = managerId?.trim() || null;
  if (next === uid) throw new Error('Someone cannot report to themselves.');
  if (next) {
    const manager = people.find(p => p.uid === next);
    if (!manager) throw new Error('That manager is not on the org chart.');
    if (!canManage(manager, person, people)) {
      throw new Error('A manager must be in the same group and a higher layer.');
    }
  }
  const resolved = deriveReportsTo({ ...person, reportsToUserId: next || undefined }, people) || null;
  await updateDoc(doc(db, 'users', uid), {
    reportsToUserId: resolved || deleteField(),
  });
}

/** Write every out-of-sync `reportsToUserId` from `reportingSyncDiff`. */
export async function applyReportingRules(rows: ReportingSyncRow[]): Promise<number> {
  const pending = rows.filter(r => r.uid.trim());
  if (pending.length === 0) return 0;
  for (let i = 0; i < pending.length; i += 400) {
    const batch = writeBatch(db);
    for (const row of pending.slice(i, i + 400)) {
      batch.update(doc(db, 'users', row.uid), {
        reportsToUserId: row.to || deleteField(),
      });
    }
    await batch.commit();
  }
  return pending.length;
}
